import React, { useEffect } from 'react';
import {
  useCart,
  useCartAddAttributes,
  useCartAddItem,
  useCartClear,
  useCurrency,
} from '@backpackjs/storefront';
import Script from 'next/script';
import { useGlobalContext } from '../../contexts';

function getPageType(resourceType) {
  const pageType = 'other';
  if (resourceType === 'home_page') {
    return 'home';
  }
  if (resourceType === 'product_page') {
    return 'product';
  }
  if (resourceType === 'collection_page') {
    return 'collection';
  }
  return pageType;
}

function maybe(f, def = undefined) {
  try {
    return f();
  } catch {
    return def;
  }
}

function transformCart(cart) {
  return cart
    ? {
        item_count: maybe(() => cart.lines.reduce((p, c) => p + c.quantity, 0)),
        attributes: cart?.attributes || [],
        items: cart.lines.map((l) => ({
          handle: l.variant.product.handle,
          price: maybe(() => parseInt(l.variant.priceV2.amount, 10)),
          product_id: maybe(() =>
            parseInt(
              l.variant.product.id.replace('gid://shopify/Product/', ''),
              10
            )
          ),
          quantity: l.quantity,
          variant_id: maybe(() =>
            parseInt(
              l.variant.id.replace('gid://shopify/ProductVariant/', ''),
              10
            )
          ),
        })),
        currency: maybe(
          () => parseInt(cart.estimatedCost.totalAmount.currencyCode, 10),
          0
        ),
        total_price: maybe(
          () => parseInt(cart.estimatedCost.totalAmount.amount, 10) * 100,
          0
        ),
        token: maybe(() => cart.id.replace('gid://shopify/Cart/', ''), ''),
      }
    : undefined;
}

function useVisuallyIo({ product, resourceType }) {
  const currency = useCurrency();
  const cart = useCart();
  const { cartClear } = useCartClear();
  const { cartAddItem } = useCartAddItem();
  const { cartAddAttributes } = useCartAddAttributes();
  const pageType = getPageType(resourceType);
  const {
    actions: { openCart },
  } = useGlobalContext();

  useEffect(() => {
    maybe(() => window.visually.onCartChanged(transformCart(cart)));
  }, [cart]);

  useEffect(() => {
    maybe(() =>
      window.visually.visuallyConnect({
        cartClear,
        initialProductId: maybe(() =>
          product.id.replace('gid://shopify/Product/', '')
        ),
        initialVariantPrice: maybe(() =>
          parseInt(product.variants[0].priceV2.amount, 10)
        ),
        initialVariantId: maybe(() =>
          product.variants[0].replace('gid://shopify/ProductVariant/', '')
        ),
        addToCart: (variantId, quantity) =>
          cartAddItem({
            merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
            quantity,
          }),
        cartAddAttributes,
        openCartDrawer: openCart,
        pageType,
        initialCurrency: currency,
        initialLocale: 'en-US',
      })
    );
  }, [pageType, currency, product]);
}

export function VisuallyIo({ page, product }) {
  useVisuallyIo({
    product,
    resourceType: page?.resourceType,
  });
  return (
    <>
      <Script
        strategy="beforeInteractive"
        rel="preconnect prefetch"
        src="https://sdk.loomi-prod.xyz/widgets/vsly-preact.min.js?k=js.10110318&e=2&s=CLEARLY_FILTERED"
      />
      <Script
        strategy="beforeInteractive"
        rel="preconnect prefetch"
        src="https://sdk.loomi-prod.xyz/v/visually-spa.js"
      />
      <Script
        strategy="afterInteractive"
        src="https://sdk.loomi-prod.xyz/v/visually-a-spa.js"
      />
      <span />
    </>
  );
}
